/**
 * State → API payloads. Serialisation mirrors the vendor widget's own serializers (observed
 * 2026-08-28) because that is the contract the server actually accepts.
 */
import { normalizeControlType, submissionShape } from '../api/normalize/controlType';
import type {
  BookingMeta,
  BookingServiceInput,
  CreateBookingRequest,
  CustomField,
  CustomFieldSubmission,
  EstimatePriceRequest,
  EstimateServiceInput,
  PaymentMethodCode,
} from '../api/types';
import { sortByOrdering, type CustomFieldValues } from './customFieldValues';
import { formatServiceDate, tenantNow } from './dates';

export interface ServiceSelection {
  serviceId: number;
  /** extraId → quantity (1 for yes/no extras) */
  extras: Record<number, number>;
  /** pricingParameterId → quantity */
  pricingParameters: Record<number, number>;
}

export interface SlotSelection {
  date: string;
  hours: number;
  minutes: number;
  arrivalWindow: number;
}

export interface UserValues {
  first_name: string;
  last_name: string;
  email: string;
}

export interface AddressValues {
  address: string;
  apt_suite: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
}

export interface FormValues extends AddressValues {
  user: UserValues;
  services: ServiceSelection[];
  slot: SlotSelection | null;
  frequencyId: number | null;
  discountCode: string;
  /** the code last applied with the Apply button; drives estimates and the booking */
  appliedDiscountCode: string | null;
  customFields: CustomFieldValues;
  paymentMethod: PaymentMethodCode | null;
}

function quantityList(map: Record<number, number>): { id: number; quantity: number }[] | null {
  const list = Object.entries(map)
    .map(([id, quantity]) => ({ id: Number(id), quantity: Math.trunc(quantity) }))
    .filter((x) => Number.isFinite(x.id) && x.quantity > 0)
    .sort((a, b) => a.id - b.id);
  return list.length ? list : null;
}

export function serializeServices(selection: ServiceSelection[]): BookingServiceInput[] {
  return selection.map((s) => ({
    id: s.serviceId,
    hourly: null,
    extras: quantityList(s.extras),
    pricing_parameters: quantityList(s.pricingParameters),
  }));
}

export interface CustomFieldSerialization {
  submissions: CustomFieldSubmission[];
  /** field ids in submission order — needed to map `custom_fields.<index>` errors back */
  ids: number[];
}

export function serializeCustomFields(
  fields: CustomField[],
  values: CustomFieldValues,
  visibleCodes: Set<string>,
): CustomFieldSerialization {
  const submissions: CustomFieldSubmission[] = [];
  const ids: number[] = [];

  for (const field of sortByOrdering(fields)) {
    if (typeof field.id !== 'number' || !field.code || !visibleCodes.has(field.code)) continue;
    const value = values[field.id];
    if (!value) continue;
    const { type } = normalizeControlType(field.control_type);
    const isOther = (optionId: number) => (field.options ?? []).some((o) => o.id === optionId && o.other === true);

    let submission: CustomFieldSubmission | null = null;
    switch (submissionShape(type)) {
      case 'none':
        break;
      case 'value':
        if (value.kind === 'checkbox') {
          if (value.checked) submission = { id: field.id, value: 'true' };
        } else if (value.kind === 'text' && value.value.trim() !== '') {
          submission = { id: field.id, value: value.value.trim() };
        }
        break;
      case 'values': {
        if (value.kind !== 'single' && value.kind !== 'multi') break;
        const optionIds = value.kind === 'single' ? (value.optionId === null ? [] : [value.optionId]) : value.optionIds;
        if (!optionIds.length) break;
        submission = {
          id: field.id,
          values: optionIds.map((optionId) => ({
            id: optionId,
            other: isOther(optionId) ? (value.other[optionId]?.trim() || null) : null,
          })),
        };
        break;
      }
    }
    if (submission) {
      submissions.push(submission);
      ids.push(field.id);
    }
  }
  return { submissions, ids };
}

export interface EstimateContext {
  timeZone: string;
  now: () => Date;
  locationId: number | null;
  /**
   * Digest of the booking being edited. Without it the API prices a reschedule as a brand-new
   * booking, so a recurring frequency with `exclude_first` drops its discount and the customer
   * is shown an inflated total. Mutually exclusive with `originalBookingId`.
   */
  bookingUuid?: string | null;
  originalBookingId?: number | null;
}

/** Null when there is nothing to price yet. Before a slot is chosen, today at noon stands in. */
export function buildEstimateRequest(
  values: Pick<FormValues, 'services' | 'slot' | 'frequencyId' | 'appliedDiscountCode'>,
  ctx: EstimateContext,
): EstimatePriceRequest | null {
  if (!values.services.length || values.frequencyId === null) return null;
  const serviceDate = values.slot
    ? formatServiceDate(values.slot.date, values.slot.hours, values.slot.minutes)
    : formatServiceDate(tenantNow(ctx.timeZone, ctx.now()).date, 12, 0);
  const request: EstimatePriceRequest = {
    service_date: serviceDate,
    frequency_id: values.frequencyId,
    services: serializeServices(values.services) as EstimateServiceInput[],
  };
  if (ctx.locationId !== null) request.location_id = ctx.locationId;
  if (values.appliedDiscountCode) request.discount_code = values.appliedDiscountCode;
  // Mutually exclusive per the vendor's schema, so never send both.
  if (ctx.bookingUuid) request.booking_uuid = ctx.bookingUuid;
  else if (ctx.originalBookingId != null) request.original_booking_id = ctx.originalBookingId;
  return request;
}

export interface BookingContext {
  fields: CustomField[];
  visibleCodes: Set<string>;
  locationId: number | null;
  recaptchaToken: string;
  stripeSetupIntentId: string | null;
  meta: BookingMeta[];
}

export interface BookingPayload {
  request: CreateBookingRequest;
  customFieldIds: number[];
}

export function buildBookingRequest(values: FormValues, ctx: BookingContext): BookingPayload {
  if (!values.slot) throw new Error('A date and time must be selected');
  if (values.frequencyId === null) throw new Error('A frequency must be selected');
  if (!values.paymentMethod) throw new Error('A payment method must be selected');
  if (values.paymentMethod === 'stripe' && !ctx.stripeSetupIntentId) throw new Error('Card setup did not complete');

  const custom = serializeCustomFields(ctx.fields, values.customFields, ctx.visibleCodes);
  const street = values.address.trim();
  const suite = values.apt_suite.trim();

  const request: CreateBookingRequest = {
    user: {
      email: values.user.email.trim(),
      first_name: values.user.first_name.trim(),
      last_name: values.user.last_name.trim(),
    },
    // The API has no apt/suite field; the vendor form folds it into the street address.
    address: suite ? `${street}, ${suite}` : street,
    city: values.city.trim() || null,
    state: values.state.trim() || null,
    zip: values.zip.trim() || null,
    phone: values.phone.trim() || null,
    sms_notifications: false,
    frequency_id: values.frequencyId,
    service_date: formatServiceDate(values.slot.date, values.slot.hours, values.slot.minutes),
    arrival_window: values.slot.arrivalWindow,
    services: serializeServices(values.services),
    payment_method: values.paymentMethod,
    custom_fields: custom.submissions,
    booking_type: 'default',
    recaptcha_token: ctx.recaptchaToken,
  };
  if (ctx.locationId !== null) request.location_id = ctx.locationId;
  if (values.appliedDiscountCode) request.discount_code = values.appliedDiscountCode;
  if (values.paymentMethod === 'stripe') request.stripe_setup_intent_id = ctx.stripeSetupIntentId;
  if (ctx.meta.length) request.meta = ctx.meta;
  return { request, customFieldIds: custom.ids };
}

export function estimateKey(request: EstimatePriceRequest | null): string | null {
  return request ? JSON.stringify(request) : null;
}
